import * as React from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import I18n from 'core/i18n'
import { InputText, CheckBox, InputRadio, Button, Spinner } from 'components/atoms'
import { IGuideFilterOptions, IAssignGuideTourSkill } from 'static/interfaces'
import { AssignedTourSkillSelector } from 'components/molecules/AssignedTourSkillSelector'
import SvgIcon from 'components/atoms/SvgIcon'
import { createEnterKeyHandler } from 'utils/form'

interface IProps {
  filterOptions: IGuideFilterOptions
  setFilterOptions: React.Dispatch<React.SetStateAction<IGuideFilterOptions>>
  setShowFilter: React.Dispatch<React.SetStateAction<boolean>>
  allAgentSkills: any[]
  allAgentTourSkills: any[]
  allAgentRanks: any[]
  loadingFilterParams: boolean
  onConfirm?: () => void
}

const AGE_OPTIONS = [
  { label: '20代', value: 20 },
  { label: '30代', value: 30 },
  { label: '40代', value: 40 },
  { label: '50代以上', value: 50 },
]

const GENDER_OPTIONS = [
  { id: 'female', label: '女性', value: '0' },
  { id: 'male', label: '男性', value: '1' },
  { id: 'other', label: 'その他', value: '2' },
]

const GuideFilter: React.VFC<IProps> = ({
  filterOptions,
  setFilterOptions,
  setShowFilter,
  allAgentSkills,
  allAgentTourSkills,
  allAgentRanks,
  loadingFilterParams,
  onConfirm,
}) => {
  const [name, setName] = React.useState(filterOptions.name)
  const [email, setEmail] = React.useState(filterOptions.email)
  const [tel, setTel] = React.useState(filterOptions.tel)
  const [ages, setAges] = React.useState(filterOptions.ages)
  const [gender, setGender] = React.useState(filterOptions.gender)
  const [statuses, setStatuses] = React.useState(filterOptions.statuses)
  const [loginStatus, setLoginStatus] = React.useState(filterOptions.login_status)

  const [tourGuideCountMin, setTourGuideCountMin] = React.useState(
    filterOptions.tour_guide_count_min
  )
  const [tourGuideCountMax, setTourGuideCountMax] = React.useState(
    filterOptions.tour_guide_count_max
  )
  const [agentSkills, setAgentSkills] = React.useState(filterOptions.agent_skills)
  const [assignedTourSkills, setAssignedTourSkills] = React.useState(
    filterOptions.assign_guide_tour_skills || []
  )
  const [agentRanks, setAgentRanks] = React.useState(filterOptions.agent_ranks)

  const handleCheckAge = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newAges = e.target.checked
        ? ages.concat([Number(e.target.value)])
        : ages.filter(age => age !== Number(e.target.value))
      setAges(newAges)
    },
    [ages]
  )

  const handleCheckSkill = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSkills = e.target.checked
        ? agentSkills.concat([Number(e.target.value)])
        : agentSkills.filter(age => age !== Number(e.target.value))
      setAgentSkills(newSkills)
    },
    [agentSkills]
  )

  const handleUpdateAssignedTourSkill = React.useCallback(
    (index: number, updatedSkill: IAssignGuideTourSkill) => {
      const updatedAssignedTourSkills = assignedTourSkills.map((skill, i) =>
        i === index ? updatedSkill : skill
      )
      setAssignedTourSkills(updatedAssignedTourSkills)
    },
    [assignedTourSkills]
  )

  const deleteAssignedTourSkill = (index: number) => {
    setAssignedTourSkills(old => old.filter((_, i) => i !== index))
  }

  const addAssignedTourSkill = React.useCallback(() => {
    setAssignedTourSkills([
      ...assignedTourSkills,
      { tour_skill_id: '', level: '', condition_type: 'gte' },
    ])
  }, [assignedTourSkills])

  const handleCheckRank = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newRanks = e.target.checked
        ? agentRanks.concat([Number(e.target.value)])
        : agentRanks.filter(rank => rank !== Number(e.target.value))
      setAgentRanks(newRanks)
    },
    [agentRanks]
  )
  const setTourGuideCountMinValue = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === '') {
        setTourGuideCountMin(null)
        return
      }
      const value = e.target.value ? Number(e.target.value) : null
      setTourGuideCountMin(value)
    },
    [tourGuideCountMax]
  )

  const setTourGuideCountMaxValue = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === '') {
        setTourGuideCountMax(null)
        return
      }
      const value = e.target.value ? Number(e.target.value) : null
      setTourGuideCountMax(value)
    },
    [tourGuideCountMin]
  )

  const validationOnBlur = React.useCallback(() => {
    // どちらもからなず0以上になるようにする
    const validatedMax = Math.max(tourGuideCountMax ?? 0, 0)
    const validatedMin = Math.max(tourGuideCountMin ?? 0, 0)

    if (validatedMax < validatedMin && tourGuideCountMax !== null) {
      setTourGuideCountMax(validatedMin)
    } else if (tourGuideCountMax < 0) {
      setTourGuideCountMax(validatedMax)
    }
    if (tourGuideCountMin !== null) {
      setTourGuideCountMin(validatedMin)
    }
  }, [tourGuideCountMin, tourGuideCountMax])

  const handleConfirm = React.useCallback(() => {
    const filteredAssignedTourSkills = assignedTourSkills.filter(
      skill => skill.tour_skill_id !== ''
    )
    setFilterOptions({
      ...filterOptions,
      name,
      email,
      tel,
      ages,
      gender,
      tour_guide_count_min: tourGuideCountMin,
      tour_guide_count_max: tourGuideCountMax,
      agent_skills: agentSkills,
      assign_guide_tour_skills: filteredAssignedTourSkills,
      agent_ranks: agentRanks,
      // 全て外した場合はサーバのデフォルト(公開のみ)と表示を揃える
      statuses: statuses.length > 0 ? statuses : constants.DEFAULT_GUIDE_FILTER_OPTIONS.statuses,
      login_status: loginStatus,
    })
    setShowFilter(false)

    if (onConfirm) {
      onConfirm()
    }
  }, [
    filterOptions,
    name,
    email,
    tel,
    ages,
    gender,
    tourGuideCountMin,
    tourGuideCountMax,
    agentRanks,
    agentSkills,
    assignedTourSkills,
    statuses,
    loginStatus,
  ])

  const handleSearchKeyDown = createEnterKeyHandler(handleConfirm)

  return (
    <Wrapper>
      <div className="scroller">
        <div className="filterCategory">
          <InputText
            name="name"
            value={name}
            onChangeHandler={e => setName(e.target.value)}
            onKeyDownHandler={handleSearchKeyDown}
            placeholder="ガイド名"
          />
        </div>
        <div className="filterCategory">
          <InputText
            name="email"
            onKeyDownHandler={handleSearchKeyDown}
            value={email}
            onChangeHandler={e => setEmail(e.target.value)}
            placeholder="メールアドレス"
          />
        </div>
        <div className="filterCategory">
          <InputText
            name="tel"
            onKeyDownHandler={handleSearchKeyDown}
            value={tel}
            onChangeHandler={e => setTel(e.target.value)}
            placeholder="電話番号"
          />
        </div>
        <div className="filterCategory">
          <div className="type">催行ツアーガイド数</div>
          <div className="tourGuideCount">
            <InputText
              placeholder="最小"
              type="number"
              name={'tour_guide_count_min'}
              value={tourGuideCountMin}
              onChangeHandler={setTourGuideCountMinValue}
              onBlurHandler={validationOnBlur}
            />
            <p>〜</p>
            <InputText
              placeholder="最大"
              type="number"
              name={'tour_guide_count_max'}
              value={tourGuideCountMax}
              onChangeHandler={setTourGuideCountMaxValue}
              onBlurHandler={validationOnBlur}
            />
          </div>
        </div>
        <div className="filterCategory">
          <div className="type">ガイドランク</div>
          {loadingFilterParams && <Spinner />}
          <div className="options gapped">
            {allAgentRanks.map((rank, index) => (
              <CheckBox
                name="agent_ranks"
                value={rank.id}
                label={rank.name}
                showLabelInline={true}
                key={index}
                onChangeHandler={handleCheckRank}
                checked={agentRanks.includes(rank.id)}
              />
            ))}
          </div>
        </div>
        <div className="filterCategory">
          <div className="type">スキル</div>
          {loadingFilterParams && <Spinner />}
          <div className="options gapped">
            {allAgentSkills.map(skill => (
              <CheckBox
                name="agent_skills"
                value={skill.id}
                label={skill.name}
                showLabelInline={true}
                key={skill.id}
                onChangeHandler={handleCheckSkill}
                checked={agentSkills.includes(skill.id)}
              />
            ))}
          </div>
        </div>
        <div className="filterCategory">
          <div className="type">ツアースキル</div>
          <div className="options gapped">
            {assignedTourSkills.map((skill, index) => (
              <AssignedTourSkillSelector
                key={`agent-job-tour-skill-${index}`}
                tourSkills={allAgentTourSkills}
                assignedTourSkill={skill}
                onChange={updatedSkill => handleUpdateAssignedTourSkill(index, updatedSkill)}
                onDelete={() => deleteAssignedTourSkill(index)}
              />
            ))}
          </div>
          <div className="addSkill" onClick={addAssignedTourSkill}>
            <SvgIcon className="icon" ariaHidden={true} role="presentation" name="add" />
            <span>ツアースキルを追加</span>
          </div>
        </div>

        <div className="filterCategory">
          <div className="type">ステータス</div>
          <div className="options gapped">
            {constants.GUIDE_STATUSES.map(status => (
              <CheckBox
                key={status}
                name="statuses"
                value={status}
                label={I18n.t(`enums.user.status.${status}`, { locale: 'ja' })}
                showLabelInline={true}
                onChangeHandler={e =>
                  setStatuses(
                    e.target.checked
                      ? statuses.concat([status])
                      : statuses.filter(s => s !== status)
                  )
                }
                checked={statuses.includes(status)}
              />
            ))}
          </div>
        </div>

        <div className="filterCategory">
          <div className="type">ログイン状況</div>
          <div className="options gapped">
            <CheckBox
              name="login_status_never"
              label="未ログインのみ"
              showLabelInline={true}
              onChangeHandler={e => setLoginStatus(e.target.checked ? 'never_logged_in' : '')}
              checked={loginStatus === 'never_logged_in'}
            />
            <CheckBox
              name="login_status_logged_in"
              label="ログイン済みのみ"
              showLabelInline={true}
              onChangeHandler={e => setLoginStatus(e.target.checked ? 'logged_in' : '')}
              checked={loginStatus === 'logged_in'}
            />
          </div>
        </div>

        <div className="filterCategory">
          <div className="type">性別</div>
          <div className="options">
            <InputRadio
              name="gender"
              onChangeHandler={e => setGender(e.target.value)}
              options={GENDER_OPTIONS}
              defaultValue={filterOptions.gender}
            />
          </div>
        </div>
        <div className="filterCategory">
          <div className="type">年齢・年代</div>
          <div className="options gapped">
            {AGE_OPTIONS.map((option, index) => (
              <CheckBox
                name="ages"
                value={option.value}
                label={option.label}
                showLabelInline={true}
                key={index}
                onChangeHandler={handleCheckAge}
                checked={ages.includes(option.value)}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="actions">
        <div className="action">
          <Button handleClick={() => setShowFilter(false)}>キャンセル</Button>
          <Button primary={true} handleClick={handleConfirm}>
            フィルター
          </Button>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  font-size: 12px;
  background-color: ${constants.COLORS.white};
  border: solid 1px ${constants.COLORS.divider};
  border-radius: 4px;
  box-shadow: ${constants.COLORS.cardShadow};
  z-index: 6001;
  .scroller {
    max-height: calc(100dvh - 26rem);
    padding: 16px 16px 80px;
    overflow-y: auto;
  }
  .filterCategory {
    margin-bottom: 20px;
    .type {
      margin-bottom: 8px;
      color: ${constants.COLORS.subText};
      font-weight: bold;
    }
    .tourGuideCount {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .options {
      display: flex;
      flex-wrap: wrap;
      &.gapped {
        gap: 8px 16px;
      }
      .CheckBox {
        margin: 0;
        .CheckBox_Wrapper {
          .CheckBox_Main {
            > label {
              span {
                font-size: 13px;
                font-weight: normal;
              }
            }
          }
        }
      }
      .InputRadio_Labels {
        gap: 8px 16px;
        label {
          span {
            font-size: 13px;
            font-weight: normal;
          }
        }
      }
      &.status {
        .CheckBox_Main > label .CheckBox_Icon .icon.filled {
          color: #f08a0d;
        }
      }
    }
    .addSkill {
      .icon {
        width: 16px;
        height: 16px;
      }
      font-size: 14px;
      margin-top: 8px;
      display: flex;
      align-items: center;
      gap: 4px;
      font-weight: bold;
      color: ${constants.COLORS.primary};
      cursor: pointer;
    }
  }
  .actions {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 16px;
    background-color: ${constants.COLORS.white};
    border-top: solid 1px ${constants.COLORS.divider};
    border-radius: 0 0 4px 4px;
    .action {
      display: flex;
      align-items: center;
      justify-content: space-between;
      button {
        width: calc(50% - 8px);
      }
    }
  }
`

export default GuideFilter
